import { useMemo } from 'react';

import { CropSquareOutlined, FileDownloadOutlined } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';

import Button from '@/components/Buttons/Button';
import UpgradeToPremiumButton from '@/components/UpgradeToPremium/UpgradeToPremiumButton';
import { spacing } from '@/constants/ui';
import useUserDateFormat from '@/hooks/useUserDateFormat';
import useUserDetails from '@/hooks/useUserDetails';
import { FactsheetDetails, FACTSHEETS_ENDPOINT } from '@/pages/Facthsheets/constants';
import useItemsTab from '@/pages/Facthsheets/FactsheetView/FactsheetWindow/FactsheetBody/Tabs/ItemsTab/useItemsTab';
import ShareButton from '@/pages/Markets/MarketsComponents/Share/ShareButton';
import { useGxTokenFromStorage } from '@/services/storage';
import downloadPDF from '@/utils/downloadPDF';
import { isValidDateString } from '@/utils/isValidDateString';

const ButtonSection = ({ factsheet }: { factsheet: FactsheetDetails }) => {
  const { isUserBasic, isUserExpired } = useUserDetails();
  const { handleAllItemsSelection, allItemsSelected } = useItemsTab(factsheet);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
      {(isUserBasic || isUserExpired) && <UpgradeToPremiumButton type='upgrade' />}
      <ShareButton
        code={factsheet.code}
        iconSize={12}
        sx={{ width: '120px' }}
        title={factsheet.title}
      />
      <Button
        color='secondary'
        disabled={allItemsSelected}
        size='medium'
        startIcon={<CropSquareOutlined />}
        variant='contained'
        onClick={() => {
          handleAllItemsSelection();
        }}
      >
        Add item(s) to selector
      </Button>
      <Button
        color='secondary'
        size='medium'
        startIcon={<FileDownloadOutlined />}
        variant='contained'
        onClick={() => {
          downloadPDF(`${FACTSHEETS_ENDPOINT}/${factsheet.code}.pdf`, `${factsheet.code}.pdf`);
        }}
      >
        Download factsheet
      </Button>
    </Box>
  );
};

const FactsheetHeader = ({ factsheet }: { factsheet: FactsheetDetails }) => {
  const gxToken = useGxTokenFromStorage();
  const dateFormat = useUserDateFormat();

  const validFrom = useMemo(() => {
    const date =
      factsheet.factsheetVersionHistory.length > 0 &&
      factsheet.factsheetVersionHistory[0].factsheetValidFrom
        ? factsheet.factsheetVersionHistory[0].factsheetValidFrom
        : '';

    if (isValidDateString(date)) {
      return dayjs(date).format(dateFormat.value.toUpperCase());
    }
    return date;
  }, [dateFormat, factsheet.factsheetVersionHistory]);

  const validTo = useMemo(() => {
    const date =
      factsheet.factsheetVersionHistory.length > 0 &&
      factsheet.factsheetVersionHistory[0].factsheetValidTo
        ? factsheet.factsheetVersionHistory[0].factsheetValidTo
        : '';

    if (isValidDateString(date)) {
      return dayjs(date).format(dateFormat.value.toUpperCase());
    }
    return date;
  }, [dateFormat, factsheet.factsheetVersionHistory]);

  const descriptionParsedLines = factsheet.description?.split('\n');

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'column',
        px: 3,
        width: '100%'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
        <Box sx={{ flexGrow: 1, mr: spacing['6'], maxWidth: 740, minWidth: 0 }}>
          <Typography variant='h3'>{factsheet.title}</Typography>
        </Box>
        <Box
          display='flex'
          sx={{ py: spacing['2'], flexShrink: 0 }}
        >
          {gxToken && <ButtonSection factsheet={factsheet} />}
        </Box>
      </Box>
      <Box>
        {descriptionParsedLines?.map(line => {
          return (
            <Typography
              key={line}
              sx={theme => ({
                mb: 0.5,
                fontSize: theme.typography.pxToRem(20)
              })}
            >
              {line}
            </Typography>
          );
        })}
      </Box>
      <Typography
        sx={{ pt: 1 }}
        variant='body1'
      >
        Factsheet version {factsheet.latestFactsheetVersion}
        {validFrom && `, valid from ${validFrom}`}
        {validTo && ` to ${validTo}`}
      </Typography>
    </Box>
  );
};
export default FactsheetHeader;
